/** <scapescene-final-screen> — the premiere view: nothing but the
 * finished film, big, with a download button underneath. For watching
 * (or projecting) the result at the end of the workshop without the
 * worksheet chrome of the film screen. */

import { assetKey } from "../data/client";
import { filmDownloadUrl } from "../data/api";
import { router } from "../control/router";
import { store } from "../store";
import { t } from "../lib/i18n";

import "../components/scapescene-player";

class WorkshopFinalScreen extends HTMLElement {
  #unsubscribe: (() => void) | null = null;
  #player: HTMLElement | null = null;

  connectedCallback(): void {
    this.#render();
    this.#unsubscribe = store.onChange(() => this.#update());
    store.startPolling();
  }

  disconnectedCallback(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  #render(): void {
    this.innerHTML = `
      <div class="screen final-screen">
        <h1 class="final-screen__title js-title"></h1>
        <div class="final-screen__stage js-stage"></div>
        <div class="final-screen__actions">
          <a class="ss-btn ss-btn--primary final-screen__download js-download" hidden>
            ${t("downloadFilm")}
          </a>
          <button type="button" class="ss-btn ss-btn--ghost js-back">
            ${t("backToStoryboard")}
          </button>
        </div>
      </div>
    `;
    this.querySelector<HTMLButtonElement>(".js-back")!.addEventListener(
      "click",
      () => router.navigate("/film"),
    );
    this.#update();
  }

  #update(): void {
    const group = store.group;

    const title = this.querySelector<HTMLElement>(".js-title");
    if (title) {
      title.textContent = group?.projectTitle || t("theFilm");
    }

    const download = this.querySelector<HTMLAnchorElement>(".js-download");
    const ready = group?.filmStatus === "ready" && !!group.stitchedUrl;
    if (download) {
      download.hidden = !ready;
      if (ready && group) download.href = filmDownloadUrl(group.code);
    }

    this.#updateStage(group, ready);
  }

  #updateStage(group: typeof store.group, ready: boolean): void {
    const stage = this.querySelector<HTMLElement>(".js-stage");
    if (!stage) return;

    const desired = ready
      ? "ready"
      : group?.filmStatus === "rendering"
        ? "rendering"
        : group?.filmStatus === "error"
          ? "error"
          : "empty";

    if (stage.dataset.state === desired) {
      // Same shape — patch the src in place so playback survives polls.
      if (desired === "ready" && this.#player && group) {
        if (
          assetKey(this.#player.getAttribute("src") ?? "") !==
          assetKey(group.stitchedUrl)
        ) {
          this.#player.setAttribute("src", group.stitchedUrl);
        }
        this.#player.setAttribute("subtitles", group.subtitlesUrl ?? "");
      }
      return;
    }

    stage.dataset.state = desired;
    stage.innerHTML = "";
    this.#player = null;

    if (desired === "ready" && group) {
      const player = document.createElement("scapescene-player");
      player.setAttribute("src", group.stitchedUrl);
      player.setAttribute("subtitles", group.subtitlesUrl ?? "");
      stage.appendChild(player);
      this.#player = player;
      return;
    }

    const hint = document.createElement("p");
    hint.className = "hint";
    hint.textContent =
      desired === "rendering"
        ? t("renderingFilm")
        : desired === "error"
          ? group?.filmError || t("filmFailed")
          : t("noFilmYetFinal");
    stage.appendChild(hint);
  }
}

if (!customElements.get("scapescene-final-screen")) {
  customElements.define("scapescene-final-screen", WorkshopFinalScreen);
}

export {};
